import { useState, useEffect, useCallback } from 'react';
import { useGetOrders, useGetOrdersMetrics } from '@/features/orders/orders.queries';
import type { OrderSortBy, OrderSortOrder, OrderSearchField, Order } from '@/features/orders/orders.types';

export const useOrdersPage = () => {
  const [search, setSearch] = useState('');
  const [search_field, setSearchField] = useState<OrderSearchField>('todos');
  const [debounced_search, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [sort_by, setSortBy] = useState<OrderSortBy>('created_at');
  const [sort_order, setSortOrder] = useState<OrderSortOrder>('desc');
  const [status_filter, setStatusFilter] = useState<number | undefined>(undefined);
  const [show_deleted, setShowDeleted] = useState(false);
  const [selected_order, setSelectedOrder] = useState<Order | null>(null);

  const [is_change_status_modal_open, setIsChangeStatusModalOpen] = useState(false);
  const [orders_to_update, setOrdersToUpdate] = useState<Order[]>([]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 400);

    return () => clearTimeout(handler);
  }, [search]);

  const { data, isLoading, isError, isFetching } = useGetOrders({
    search: debounced_search || undefined,
    search_field,
    page,
    limit,
    sort_by,
    sort_order,
    status: status_filter,
    deleted: show_deleted,
  });

  const { data: metrics, isLoading: isLoadingMetrics, isFetching: isFetchingMetrics } = useGetOrdersMetrics();

  const onSort = (field: OrderSortBy) => {
    if (field === sort_by) {
      setSortOrder(sort_order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const onPageChange = (new_page: number) => {
    setPage(new_page);
  };

  const onSelectOrder = useCallback((order: Order | null) => {
    setSelectedOrder(order);
  }, []);

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? page,
    metrics,
    isLoading: isLoading || isLoadingMetrics,
    isFetching: isFetching || isFetchingMetrics,
    isError,
    search,
    search_field,
    sort_by,
    sort_order,
    status_filter,
    show_deleted,
    limit,
    selected_order,
    is_change_status_modal_open,
    orders_to_update,
    onSearchChange: setSearch,
    onSearchFieldChange: (field: OrderSearchField) => {
      setSearchField(field);
      setPage(1);
    },
    onSort,
    onPageChange,
    onLimitChange: (n: number) => {
      setLimit(n);
      setPage(1);
    },
    onStatusFilterChange: (s: number | undefined) => {
      setStatusFilter(s);
      setPage(1);
    },
    onToggleShowDeleted: () => {
      setShowDeleted((prev) => !prev);
      setPage(1);
    },
    onSelectOrder,
    onOpenChangeStatusModal: (orders: Order[]) => {
      setOrdersToUpdate(orders);
      setIsChangeStatusModalOpen(true);
    },
    onCloseChangeStatusModal: () => {
      setOrdersToUpdate([]);
      setIsChangeStatusModalOpen(false);
    },
  };
};
